// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { Form, ResetFormButton, SubmitFormButton, TextField, useFormContext } from '@calendly/form'
import { object, string } from '@calendly/form/validation'
import { usePageTitle } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { PageHeader } from '@calendly/ui/components/page-header'
import { ProTip } from '@calendly/ui/components/pro-tip'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { colorTextSecondary } from '@calendly/ui/theme'
import { isEqual } from 'lodash-es'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DirtyFormTracking } from 'components/core/form'
import { ResetFormOnSave } from 'components/core/form/reset-form-on-save'
import { PageContainer } from 'components/page_elements'
import { CUSTOM_SMS_LEGAL_DISCLAIMER } from 'constants/billing_features'
import { isAvailableFeatureSelector } from 'features/billing'
import { nameSelector, ORGANIZATION_NAME_MAX_LENGTH } from 'features/organization_settings'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'
import { orgLogoUrlSelector } from 'selectors/current_user'


import { CheckCanUnbrand } from './check_can_unbrand'
import { LogoField } from './logo_field'
import { SMS_LEGAL_DISCLAIMER_MAX_LENGTH } from '../../constants'
import { fetchSettings, saveOrganizationBrandingSettings } from '../../slice'
import { LogoTipImage } from './logo_field/logo_tip_image'
import { SmsLegalDisclaimerComponent } from './sms_legal_disclaimer/component'
import { FormButtons, FormContainer, FormRow, StyledFormRow, TitleContentWrapper } from './styles'
import {
  brandingSaveRequestCompletedRequestSelector,
  canEnforceLogoSelector,
  isBrandedSelector,
  smsLegalDisclaimerSelector
} from '../../selectors'


const NameField = () => {
  const { errors } = useFormContext()

  return (
    <TextField
      placeholder={I18n.t('publisher.organization_settings.name.form.placeholder')}
      name="name"
      error={errors.name}
    />
  )
}

const SubmitButton = () => {
  const { dirty, errors } = useFormContext()

  const disabled = !dirty || Object.keys(errors).length > 0

  return (
    <SubmitFormButton disabled={disabled}>
      {I18n.t('publisher.action.save_changes')}
    </SubmitFormButton>
  )
}

export const OrganizationBranding = () => {
  const dispatch = useDispatch()
  const inSettingsModal = useIsSettingsModalLayout()

  const hasSmsLegalDisclaimerAccess = useSelector((state) => isAvailableFeatureSelector(state, { featureKey: CUSTOM_SMS_LEGAL_DISCLAIMER }))

  const isFormSaved = useSelector(brandingSaveRequestCompletedRequestSelector)
  const name = useSelector(nameSelector)
  const canEnforceLogo = useSelector(canEnforceLogoSelector)
  const logo = useSelector(orgLogoUrlSelector)
  const branded = useSelector(isBrandedSelector)
  const smsLegalDisclaimer = useSelector(smsLegalDisclaimerSelector)

  useEffect(() => {
    dispatch(fetchSettings())
  }, [dispatch])

  const initialValues = useMemo(() => ({
    name,
    logo,
    branded,
    removeLogo: false,
    cropData: '',
    legalDisclaimer: smsLegalDisclaimer || ''
  }), [name, logo, branded, smsLegalDisclaimer])

  usePageTitle(
    `${I18n.t('publisher.account_settings.navigation.branding')} - ${I18n.t('publisher.account_settings.profile.header')} - Calendly`
  )

  const onSubmit = useCallback((values) => {
    if (isEqual(initialValues, values)) return null

    const changedValues = Object.fromEntries(Object.entries(values).filter(([type, value]) => value !== initialValues[type]))

    const enforceLogo = canEnforceLogo && !!changedValues.logo

    const { legalDisclaimer: newLegalDisclaimer } = changedValues
    let disclaimer

    if (!hasSmsLegalDisclaimerAccess || newLegalDisclaimer === undefined) {
      disclaimer = undefined
    } else {
      disclaimer = newLegalDisclaimer.trim().length > 0 ? newLegalDisclaimer.trim() : null
    }

    return dispatch(
      saveOrganizationBrandingSettings({
        ...changedValues,
        enforceLogo,
        legalDisclaimer: disclaimer
      })
    )
  }, [dispatch, initialValues, canEnforceLogo, hasSmsLegalDisclaimerAccess])

  const validationSchema = useMemo(() => (
    object({
      name: string().max(ORGANIZATION_NAME_MAX_LENGTH).nullable(true),
      legalDisclaimer: string()
        .matches(/\S/, I18n.t('publisher.organization.branding.sms_legal_disclaimer.whitespace_validation_error'))
        .max(SMS_LEGAL_DISCLAIMER_MAX_LENGTH)
        .nullable(true)
    })
  ), [])

  return (
    <>
      {!inSettingsModal && (
        <PageHeader
          title={I18n.t('publisher.organization.branding.title')}
          eyebrow={(
            <BodyText weight="bold" color={colorTextSecondary}>
              {I18n.t('publisher.admin.navigation.organization')}
            </BodyText>
          )}
        />
      )}
      <PageContainer>
        <FormContainer data-testid="branding-form">
          <Form
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnChange
            enableReinitialize
          >
            <StyledFormRow>
              <TitleText level={2} size="medium">{I18n.t('publisher.organization_settings.name.header')}</TitleText>
              <BodyText>{I18n.t('publisher.organization_settings.name.description')}</BodyText>
              <NameField />
            </StyledFormRow>
            <CheckCanUnbrand />
            <DirtyFormTracking />
            <ResetFormOnSave isSaved={isFormSaved} />
            <FormRow>

              <TitleText level={2} size="medium">
                <TitleContentWrapper>
                  {I18n.t('publisher.admin.branding.org_logo_form.title')}
                  {' '}
                  <ProTip text={I18n.t('publisher.admin.branding.org_logo_form.example_link')}>
                    <>
                      {I18n.t('publisher.account_settings.branding.logo_tip.part1')}
                      <LogoTipImage />
                      {I18n.t('publisher.account_settings.branding.logo_tip.part2')}
                    </>
                  </ProTip>
                </TitleContentWrapper>
              </TitleText>

              <LogoField isAdminIA />
            </FormRow>
            <SmsLegalDisclaimerComponent />
            <FormButtons>
              <SubmitButton />
              <ResetFormButton>{I18n.t('publisher.action.cancel')}</ResetFormButton>
            </FormButtons>
          </Form>
        </FormContainer>
      </PageContainer>
    </>
  )
}
